import { z } from 'zod';
import { isoDateSchema } from './common.js';
import { userRoleSchema } from './auth.js';

export const projectStatusSchema = z.enum([
  'planning',
  'in_progress',
  'on_hold',
  'completed',
  'archived',
]);
export type ProjectStatus = z.infer<typeof projectStatusSchema>;

/**
 * Wave 14 (Onda C) · projects.tipo_obra · destrava o B10 end-to-end.
 *
 * Quatro nichos atendidos hoje. Coluna no banco é TEXT livre (migration
 * 0230) — o enum aqui é a fonte de verdade pra UI e service, mas o banco
 * aceita NULL pra "não informado / sem preferência" (default; campo
 * opcional na criação). Quando preenchido, o `TrenaTemplatePickerDrawer`
 * agrupa templates específicos no topo via `?obra_type=`.
 */
export const tipoObraSchema = z.enum([
  'residencial_popular',
  'residencial_medio',
  'comercial_loja',
  'industrial_galpao',
]);
export type TipoObra = z.infer<typeof tipoObraSchema>;

/**
 * Wave 14 (Onda C) · projects.region · destrava o B14 SINAPI regional.
 *
 * Aceita 'BR' (nacional CAIXA · default · compat retro pre-migration)
 * ou UF de 2 letras maiúsculas (SP, RJ, BA, AM, RS, ... · 27 UFs do
 * Brasil). Validação no app layer (Zod) · banco é TEXT livre (mesma
 * estratégia de `tipo_obra` · evita migration por UF nova).
 *
 * Quando setado pra UF, o `runSinapiCheck` server-side consulta
 * preço dessa UF primeiro (fallback BR). Diferença típica entre
 * UFs: 30%+ no mesmo insumo SINAPI.
 */
export const projectRegionSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine(
    (v) =>
      v === 'BR' ||
      /^[A-Z]{2}$/.test(v),
    { message: 'Use "BR" (nacional) ou UF de 2 letras (SP, RJ, ...).' },
  );
export type ProjectRegion = z.infer<typeof projectRegionSchema>;

/**
 * URL-safe slug for public-facing routes (showcase, share-links). Lowercase
 * a-z0-9 with single hyphens between segments, 2-80 chars. Stored in
 * `projects.slug` (UNIQUE INDEX `idx_projects_slug`). Optional on a project
 * — set later via PATCH when the owner is ready to publish a showcase.
 */
export const projectSlugSchema = z
  .string()
  .trim()
  .min(2)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug deve conter apenas letras minúsculas, números e hífens.');

export const projectSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: z.string(),
  clientName: z.string(),
  clientUserId: z.string().nullable(),
  status: projectStatusSchema,
  slug: z.string().nullable(),
  startDate: isoDateSchema.nullable(),
  estimatedEndDate: isoDateSchema.nullable(),
  actualEndDate: isoDateSchema.nullable(),
  budgetCents: z.number().int().nonnegative().nullable(),
  description: z.string().nullable(),
  coverPhotoUrl: z.string().url().nullable(),
  progressPercent: z.number().min(0).max(100),
  /**
   * FIX-380TC-33 (P0) · marks this project as test/E2E data. When `true`,
   * downstream fan-out paths (showcase lead notifications, procurement
   * autopilot, etc.) suppress side-effects targeted at real users. Default
   * `false` (production). Set to `true` by E2E agents in their POST body.
   */
  isTestData: z.boolean(),
  /**
   * Wave 14 (Onda C) · B10 end-to-end. Tipo de obra escolhido pelo PM na
   * criação/edição (opcional). Quando setado, o First Run Wizard do Trena
   * filtra templates específicos do tipo. `null` = "não informado".
   */
  tipoObra: tipoObraSchema.nullable(),
  /**
   * Wave 14 (Onda C) · B14 SINAPI regional. UF da obra ('BR' nacional ou
   * 2 letras maiúsculas). Default 'BR' (migration 0233 backfilla todos
   * projetos legados pra 'BR'). Quando setado pra UF, o cross-check
   * SINAPI usa preço daquele estado primeiro (fallback BR).
   */
  region: projectRegionSchema,
  /**
   * Wave geo · 2026-05-21 (migration 0250) — coordenadas geocoded do
   * endereço. Preenchidos automaticamente pelo backend via Google Geocoding
   * quando o endereço muda, OU pelo frontend quando o PM selecionou um lugar
   * no autocomplete (passa lat/lng/placeId no payload pra evitar 2ª chamada).
   * `null` enquanto o endereço não foi geocoded com sucesso. UI usa esses
   * campos pra renderizar thumbnail estático / mapa embed.
   */
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
  placeId: z.string().nullable().optional(),
  addressNormalized: z.string().nullable().optional(),
  cep: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Project = z.infer<typeof projectSchema>;

export const createProjectRequestSchema = z.object({
  name: z.string().trim().min(2).max(160),
  address: z.string().trim().min(2).max(240),
  clientName: z.string().trim().min(2).max(160),
  clientEmail: z.string().trim().toLowerCase().email().optional(),
  description: z.string().trim().max(2000).optional(),
  startDate: isoDateSchema.optional(),
  estimatedEndDate: isoDateSchema.optional(),
  budgetCents: z.number().int().nonnegative().optional(),
  /**
   * FIX-380TC-33 (P0) · opt-in flag for E2E/automation. Setting `true`
   * tells downstream services to skip side-effects (emails, inbox
   * fan-out, billing rollups) directed at real users. Defaults to
   * `false` server-side when omitted — only test agents pass `true`.
   */
  isTestData: z.boolean().optional(),
  /**
   * Wave 14 (Onda C) · B10. Tipo de obra (opcional). Quando informado,
   * destrava o filtro de templates recomendados no First Run Wizard.
   * Validado contra `tipoObraSchema`; valor desconhecido → 400 com
   * mensagem humana.
   */
  tipoObra: tipoObraSchema.optional(),
  /**
   * Wave 14 (Onda C) · B14. UF da obra (opcional). Quando informada,
   * destrava o lookup hierárquico SINAPI no `runSinapiCheck` (UF →
   * fallback BR). Validado contra `projectRegionSchema`.
   */
  region: projectRegionSchema.optional(),
  /**
   * Wave geo · 2026-05-21. Quando o PM já escolheu um lugar via Google
   * Places Autocomplete no client, passamos lat/lng/placeId pra skipar a
   * 2ª chamada geocoding no backend. Quando omitidos, o backend tenta
   * geocodificar `address` automaticamente.
   */
  lat: z.number().optional(),
  lng: z.number().optional(),
  placeId: z.string().optional(),
});
export type CreateProjectRequest = z.infer<typeof createProjectRequestSchema>;

export const updateProjectRequestSchema = createProjectRequestSchema
  .partial()
  .extend({
    status: projectStatusSchema.optional(),
    actualEndDate: isoDateSchema.optional(),
    coverPhotoUrl: z.string().url().optional(),
    /**
     * Wave 380TC FIX-12: previously the PATCH allow-list silently
     * dropped `slug`, so clients got a 200 with no persisted change and
     * downstream `POST /v1/showcases` failed with "projeto precisa ter
     * um slug". Now whitelisted with the canonical project-slug shape.
     */
    slug: projectSlugSchema.optional(),
    /**
     * Wave 14 (Onda C) · B10. PATCH aceita `null` pra limpar (PM mudou de
     * ideia ou setou errado). `undefined` = não toca; enum value =
     * sobrescreve. Override do `.partial()` herdado pra liberar null.
     */
    tipoObra: tipoObraSchema.nullable().optional(),
    /**
     * Wave 14 (Onda C) · B14. PATCH aceita string UF ou 'BR'. Diferente
     * de `tipoObra`, `region` não é nullable (banco tem NOT NULL DEFAULT
     * 'BR') · pra "limpar", o PM passa 'BR' explicitamente.
     */
    region: projectRegionSchema.optional(),
  });
export type UpdateProjectRequest = z.infer<typeof updateProjectRequestSchema>;

/**
 * Free-text specialization shown next to a member's name ("Engenheiro civil",
 * "Pedreiro", "Ajudante", "Eletricista"...). This is descriptive only — role
 * governs permissions, `trade` is just a label for the team list.
 */
export const tradeSchema = z.string().trim().min(1).max(80);

/**
 * A user on a project. One user may hold MULTIPLE roles on the same project
 * (e.g. a PM who also works as foreman on that build), which is why `roles`
 * is an array. The API aggregates the underlying per-role rows into one entry
 * per (project, user) pair for the UI.
 */
export const projectMemberSchema = z.object({
  projectId: z.string(),
  userId: z.string(),
  roles: z.array(userRoleSchema).min(1),
  /** Optional specialization (see tradeSchema). */
  trade: z.string().nullable(),
  /**
   * Wave 314c — decorative per-project scope label. Drives the yellow
   * "you're acting outside your lane" banner. Auth still uses `users.role`,
   * so this is UX, not authz. Today values are NULL or 'financeiro'.
   */
  scopeLabel: z.string().nullable(),
  fullName: z.string(),
  email: z.string().email(),
  avatarUrl: z.string().url().nullable(),
  /**
   * Projected from `users.pix_key` so the team card can render a "pay via
   * PIX" affordance without an extra lookup. Null when the member hasn't
   * set a PIX key in Settings yet.
   */
  pixKey: z.string().nullable(),
  /** Earliest invited_at across all of this user's role rows. */
  invitedAt: z.string(),
});
export type ProjectMember = z.infer<typeof projectMemberSchema>;

/**
 * Add an existing user to a project with a single role. To grant multiple
 * roles (e.g. PM + foreman), call this endpoint once per role, or pass the
 * `additionalRoles` array.
 */
export const addProjectMemberRequestSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  role: userRoleSchema,
  additionalRoles: z.array(userRoleSchema).optional(),
  trade: tradeSchema.nullable().optional(),
});
export type AddProjectMemberRequest = z.infer<typeof addProjectMemberRequestSchema>;

/**
 * Replace the full set of roles a user has on the project. The service diffs
 * against existing rows and inserts/deletes accordingly. `trade` is also
 * replaced when provided.
 */
export const updateProjectMemberRequestSchema = z.object({
  roles: z.array(userRoleSchema).min(1),
  trade: tradeSchema.nullable().optional(),
});
export type UpdateProjectMemberRequest = z.infer<typeof updateProjectMemberRequestSchema>;

/**
 * Wave 314c — set/clear the per-project scope label. Decorative only;
 * authorization unchanged. `null` (or empty string normalized to null)
 * removes the label.
 */
export const updateProjectMemberScopeRequestSchema = z.object({
  scopeLabel: z.string().trim().min(1).max(40).nullable(),
});
export type UpdateProjectMemberScopeRequest = z.infer<
  typeof updateProjectMemberScopeRequestSchema
>;
